import { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import ProfileDialog from '@/components/ProfileDialog';
import {
  LayoutDashboard, Upload, Bookmark, LogOut, ChevronLeft, ChevronRight, Menu, ArrowLeftRight, BookOpen, Target, MessageSquare, BarChart3, Link2, MessageCircle, CalendarDays,
} from 'lucide-react';
import NotificationBell from '@/components/social/NotificationBell';

const sidebarGroups = [
  {
    label: 'Content',
    items: [
      { path: '/social', icon: LayoutDashboard, label: 'Dashboard' },
      { path: '/social/calendar', icon: CalendarDays, label: 'Calendar' },
      { path: '/social/upload', icon: Upload, label: 'New Post' },
    ],
  },
  {
    label: 'Engage',
    items: [
      { path: '/social/messaging', icon: MessageCircle, label: 'Messages' },
      { path: '/social/whatsapp', icon: MessageSquare, label: 'WhatsApp' },
    ],
  },
  {
    label: 'Grow',
    items: [
      { path: '/social/analytics', icon: BarChart3, label: 'Analytics' },
      { path: '/social/blog', icon: BookOpen, label: 'Blog Generator' },
      { path: '/social/competitors', icon: Target, label: 'Competitors' },
    ],
  },
  {
    label: 'Manage',
    items: [
      { path: '/social/templates', icon: Bookmark, label: 'Templates' },
      { path: '/social/connections', icon: Link2, label: 'Connections' },
    ],
  },
];

const SocialSidebarNav = ({ collapsed, onNavClick }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'Admin';
  const [profileOpen, setProfileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <>
      {/* Admin: Back to LMS */}
      {isAdmin && (
        <div className="px-2 pt-3">
          <button
            onClick={() => { onNavClick(); navigate('/dashboard'); }}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors w-full text-indigo-400 hover:text-white hover:bg-indigo-600/30 border border-indigo-500/30",
              collapsed && "justify-center"
            )}
            data-testid="back-to-lms-btn"
            title="Switch to LMS"
          >
            <ArrowLeftRight className="h-5 w-5 flex-shrink-0" />
            {!collapsed && <span className="text-sm font-medium">Switch to LMS</span>}
          </button>
        </div>
      )}

      <nav className="flex-1 py-3 overflow-y-auto">
        {sidebarGroups.map((group) => (
          <div key={group.label} className="mb-3">
            {!collapsed && (
              <p className="px-4 mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{group.label}</p>
            )}
            {collapsed && <div className="border-t border-slate-700/50 mx-3 my-1" />}
            <ul className="space-y-0.5 px-2">
              {group.items.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    end={item.path === '/social'}
                    onClick={onNavClick}
                    className={({ isActive }) => cn(
                      "flex items-center gap-3 px-3 py-2 rounded-md transition-colors",
                      isActive
                        ? "bg-indigo-600 text-white"
                        : "text-slate-400 hover:text-white hover:bg-slate-800",
                      collapsed && "justify-center"
                    )}
                    title={item.label}
                    data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <item.icon className="h-4.5 w-4.5 flex-shrink-0" />
                    {!collapsed && <span className="text-sm">{item.label}</span>}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className="p-4 border-t border-slate-700">
        <button
          onClick={() => setProfileOpen(true)}
          className={cn(
            "flex items-center gap-3 w-full text-left rounded-md hover:bg-slate-800 p-1.5 -m-1.5 transition-colors",
            collapsed && "justify-center"
          )}
          data-testid="social-profile-btn"
          title="Edit Profile"
        >
          <Avatar className="h-8 w-8">
            <AvatarImage src={user?.picture} alt={user?.name} />
            <AvatarFallback className="bg-indigo-600 text-white text-xs">
              {user?.name?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">Social Media</p>
            </div>
          )}
        </button>
        <Button
          variant="ghost"
          size={collapsed ? "icon" : "sm"}
          onClick={handleLogout}
          className={cn(
            "mt-3 text-slate-400 hover:text-white hover:bg-slate-800",
            collapsed ? "w-full" : "w-full justify-start gap-2"
          )}
        >
          <LogOut className="h-4 w-4" />
          {!collapsed && <span>Logout</span>}
        </Button>
      </div>
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
    </>
  );
};

export const SocialLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className={cn(
        "hidden lg:flex flex-col h-screen bg-slate-900 text-white transition-all duration-300",
        collapsed ? "w-16" : "w-56"
      )} data-testid="social-desktop-sidebar">
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          {!collapsed && (
            <h1 className="font-bold text-base tracking-tight text-indigo-400">Social Hub</h1>
          )}
          <Button
            variant="ghost" size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>
        <SocialSidebarNav collapsed={collapsed} onNavClick={() => {}} />
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-64 bg-slate-900 text-white border-slate-700">
          <SheetTitle className="sr-only">Social Media Navigation</SheetTitle>
          <div className="flex items-center gap-2 p-4 border-b border-slate-700">
            <h1 className="font-bold text-base text-indigo-400">Social Hub</h1>
          </div>
          <SocialSidebarNav collapsed={false} onNavClick={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top bar */}
        <header className="h-14 border-b border-slate-200 flex items-center px-4 bg-white shrink-0" data-testid="social-header">
          <Button variant="ghost" size="icon" className="lg:hidden mr-2" onClick={() => setMobileOpen(true)}>
            <Menu className="h-5 w-5" />
          </Button>
          <h2 className="text-sm font-semibold text-slate-700 flex-1">Social Media Content Hub</h2>
          <NotificationBell />
        </header>
        <main className="flex-1 overflow-auto bg-slate-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
